
import React, { createContext, useContext, useReducer, useEffect } from 'react';

const WishlistContext = createContext();

// Wishlist reducer
const wishlistReducer = (state, action) => {
  switch (action.type) {
    case 'ADD_TO_WISHLIST':
      console.log('🔄 Wishlist Reducer: ADD_TO_WISHLIST', action.payload);
      const existingItem = state.items.find(item => item.id === action.payload.id);
      if (existingItem) {
        console.log('⚠️ Item already in wishlist, not adding duplicate');
        return {
          ...state,
          items: state.items.map(item =>
            item.id === action.payload.id ? item : item
          )
        };
      }
      const newState = {
        ...state,
        items: [...state.items, action.payload]
      };
      console.log('✅ Item added to wishlist. Total items:', newState.items.length);
      return newState;

    case 'REMOVE_FROM_WISHLIST':
      console.log('🔄 Wishlist Reducer: REMOVE_FROM_WISHLIST', action.payload);
      const filteredItems = state.items.filter(item => item.id !== action.payload);
      console.log('✅ Item removed from wishlist. Remaining items:', filteredItems.length);
      return {
        ...state,
        items: filteredItems
      };

    case 'CLEAR_WISHLIST':
      console.log('🔄 Wishlist Reducer: CLEAR_WISHLIST');
      return {
        ...state,
        items: []
      };

    case 'LOAD_WISHLIST':
      console.log('🔄 Wishlist Reducer: LOAD_WISHLIST', action.payload);
      return {
        ...state,
        items: action.payload || []
      };

    case 'WISHLIST_ERROR':
      console.error('❌ Wishlist Reducer Error:', action.payload);
      return state;

    default:
      console.log('⚠️ Unknown wishlist action:', action.type);
      return state;
  }
};

const initialState = {
  items: []
};

export const WishlistProvider = ({ children }) => {
  const [state, dispatch] = useReducer(wishlistReducer, initialState);

  // Load wishlist from localStorage on mount
  useEffect(() => {
    console.log('🔄 WishlistProvider: Loading initial wishlist from localStorage');
    try {
      const savedWishlist = localStorage.getItem('wishlist');
      console.log('📦 Saved wishlist data:', savedWishlist);
      
      if (savedWishlist) {
        const wishlistData = JSON.parse(savedWishlist);
        console.log('✅ Parsed wishlist data:', wishlistData);
        
        if (Array.isArray(wishlistData)) {
          dispatch({ type: 'LOAD_WISHLIST', payload: wishlistData });
          console.log('✅ Loaded', wishlistData.length, 'items from localStorage');
        } else {
          console.error('❌ Invalid wishlist data format in localStorage');
          dispatch({ type: 'WISHLIST_ERROR', payload: 'Invalid wishlist format' });
        }
      } else {
        console.log('ℹ️ No saved wishlist found, starting with empty wishlist');
      }
    } catch (error) {
      console.error('❌ Error loading wishlist from localStorage:', error);
      dispatch({ type: 'WISHLIST_ERROR', payload: error.message });
    }
  }, []);

  // Save wishlist to localStorage whenever it changes
  useEffect(() => {
    console.log('💾 WishlistProvider: Saving wishlist to localStorage', state.items);
    try {
      localStorage.setItem('wishlist', JSON.stringify(state.items));
      console.log('✅ Wishlist saved to localStorage successfully');
    } catch (error) {
      console.error('❌ Error saving wishlist to localStorage:', error);
    }
  }, [state.items]);

  // Save wishlist to user profile if user is logged in
  useEffect(() => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        console.log('👤 User found, saving user-specific wishlist:', user.id);
        
        if (user && user.id && state.items.length >= 0) {
          // Save to user's profile in localStorage
          const userProfileKey = `user_${user.id}_wishlist`;
          localStorage.setItem(userProfileKey, JSON.stringify(state.items));
          console.log('✅ User wishlist saved with key:', userProfileKey);
        }
      } else {
        console.log('👤 No user logged in, using general wishlist');
      }
    } catch (error) {
      console.error('❌ Error managing user-specific wishlist:', error);
    }
  }, [state.items]);

  const addToWishlist = (product) => {
    console.log('🎯 addToWishlist called with product:', product);
    
    if (!product) {
      console.error('❌ Cannot add null/undefined product to wishlist');
      return;
    }
    
    if (!product.id) {
      console.error('❌ Cannot add product without ID to wishlist:', product);
      return;
    }

    try {
      dispatch({ type: 'ADD_TO_WISHLIST', payload: product });
      console.log('✅ Product dispatched to wishlist reducer');
    } catch (error) {
      console.error('❌ Error adding to wishlist:', error);
    }
  };

  const removeFromWishlist = (productId) => {
    console.log('🎯 removeFromWishlist called with ID:', productId);
    
    if (!productId) {
      console.error('❌ Cannot remove product without ID from wishlist');
      return;
    }

    try {
      dispatch({ type: 'REMOVE_FROM_WISHLIST', payload: productId });
      console.log('✅ Product removal dispatched to wishlist reducer');
    } catch (error) {
      console.error('❌ Error removing from wishlist:', error);
    }
  };

  const clearWishlist = () => {
    console.log('🎯 clearWishlist called');
    
    try {
      dispatch({ type: 'CLEAR_WISHLIST' });
      console.log('✅ Clear wishlist dispatched to reducer');
    } catch (error) {
      console.error('❌ Error clearing wishlist:', error);
    }
  };

  const isInWishlist = (productId) => {
    if (!productId) {
      console.warn('⚠️ isInWishlist called with null/undefined productId');
      return false;
    }
    
    const exists = state.items.some(item => item.id === productId);
    console.log('🔍 isInWishlist check for ID', productId, ':', exists);
    return exists;
  };

  const toggleWishlist = (product) => {
    console.log('🎯 toggleWishlist called with product:', product);
    
    if (!product || !product.id) {
      console.error('❌ Cannot toggle wishlist for invalid product:', product);
      return;
    }

    if (isInWishlist(product.id)) {
      console.log('📤 Removing from wishlist (toggle)');
      removeFromWishlist(product.id);
    } else {
      console.log('📥 Adding to wishlist (toggle)');
      addToWishlist(product);
    }
  };

  const getWishlistItemsCount = () => {
    const count = state.items.length;
    console.log('🔢 getWishlistItemsCount:', count);
    return count;
  };

  // Load user's wishlist when they log in
  const loadUserWishlist = (userId) => {
    console.log('🎯 loadUserWishlist called for user:', userId);
    
    if (!userId) {
      console.error('❌ Cannot load wishlist for null/undefined user ID');
      return;
    }

    try {
      const userProfileKey = `user_${userId}_wishlist`;
      console.log('🔑 Looking for user wishlist with key:', userProfileKey);
      
      const savedUserWishlist = localStorage.getItem(userProfileKey);
      console.log('📦 User wishlist data found:', savedUserWishlist);
      
      if (savedUserWishlist) {
        const wishlistData = JSON.parse(savedUserWishlist);
        console.log('✅ Parsed user wishlist:', wishlistData);
        
        if (Array.isArray(wishlistData)) {
          dispatch({ type: 'LOAD_WISHLIST', payload: wishlistData });
          console.log('✅ Loaded user wishlist with', wishlistData.length, 'items');
        } else {
          console.error('❌ Invalid user wishlist format');
          dispatch({ type: 'WISHLIST_ERROR', payload: 'Invalid user wishlist format' });
        }
      } else {
        console.log('ℹ️ No saved user wishlist found, keeping current wishlist');
      }
    } catch (error) {
      console.error('❌ Error loading user wishlist:', error);
      dispatch({ type: 'WISHLIST_ERROR', payload: error.message });
    }
  };

  // Debug function to check wishlist state
  const debugWishlist = () => {
    console.log('🔍 Wishlist Debug Info:');
    console.log('- Current items count:', state.items.length);
    console.log('- Current items:', state.items);
    console.log('- localStorage wishlist:', localStorage.getItem('wishlist'));
    
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const user = JSON.parse(userStr);
      console.log('- User-specific wishlist:', localStorage.getItem(`user_${user.id}_wishlist`));
    }
  };

  // Expose debug function globally for troubleshooting
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.debugWishlist = debugWishlist;
      console.log('🔍 Debug function window.debugWishlist() is now available');
    }
  }, [state.items]);

  return (
    <WishlistContext.Provider value={{
      wishlist: state,
      addToWishlist,
      removeFromWishlist,
      clearWishlist,
      isInWishlist,
      toggleWishlist,
      getWishlistItemsCount,
      loadUserWishlist,
      debugWishlist
    }}>
      {children}
    </WishlistContext.Provider>
  );
};

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error('useWishlist must be used within a WishlistProvider');
  }
  return context;
};
